import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useDebounce } from 'use-debounce';
import { Button, Input, Select, Space, Table, Typography } from 'antd';
// Api
import { GET_TICKET_SCAN_HISTORY } from 'api/ticketing/queries';
// Types
import {
  GetTicketScanHistory,
  GetTicketScanHistoryVariables,
  GetTicketScanHistory_ticketScanHistory_entities,
} from 'api/ticketing/types/GetTicketScanHistory';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  TicketRedemptionResult,
  TicketScanSort,
} from 'api/graphql-global-types';
// Hooks
import { useServerTable } from 'pages/Events/hooks/useServerTable';
// Helpers
import {
  formatEventDateTime,
  pluralize,
  SCAN_RESULT_FILTER_OPTIONS,
  SCAN_RESULT_META,
} from 'pages/Events/helpers';
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import StatusTag from 'pages/Events/components/StatusTag/StatusTag';
// Styles
import styles from './DoorLogTab.module.scss';

const { Text } = Typography;

type Scan = GetTicketScanHistory_ticketScanHistory_entities;

const PAGE_SIZE = 50;

const SORT_COLUMNS: Record<string, TicketScanSort> = {
  scannedAt: TicketScanSort.ScannedAt,
  result: TicketScanSort.Result,
};

type DoorLogTabProps = {
  event: TicketEventDetailsFields;
};

const DoorLogTab = ({ event }: DoorLogTabProps): JSX.Element => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<TicketRedemptionResult[]>([]);
  const table = useServerTable({
    sortColumns: SORT_COLUMNS,
    defaultSortBy: TicketScanSort.ScannedAt,
    defaultPageSize: PAGE_SIZE,
  });
  const [debouncedSearch] = useDebounce(search.trim(), 400);

  const { data, loading, error, refetch } = useQuery<
    GetTicketScanHistory,
    GetTicketScanHistoryVariables
  >(GET_TICKET_SCAN_HISTORY, {
    variables: {
      input: {
        ticketEventId: event.id,
        search: debouncedSearch || null,
        results: results.length ? results : null,
        sortBy: table.sortBy,
        direction: table.direction,
        offset: table.offset,
        limit: table.pageSize,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return (
    <>
      <PageToolbar
        leading={
          <Space wrap>
            <Input.Search
              allowClear
              aria-label="Search scans"
              placeholder="Ticket number or holder"
              value={search}
              onChange={(changeEvent) => {
                setSearch(changeEvent.target.value);
                table.setPage(1);
              }}
              className={styles.search}
            />
            <Select<TicketRedemptionResult[]>
              mode="multiple"
              allowClear
              aria-label="Filter by result"
              placeholder="All results"
              value={results}
              options={SCAN_RESULT_FILTER_OPTIONS}
              onChange={(value) => {
                setResults(value);
                table.setPage(1);
              }}
              className={styles.resultFilter}
            />
          </Space>
        }
      >
        <Button onClick={() => refetch()}>Refresh</Button>
      </PageToolbar>

      {error ? (
        <LoadErrorAlert message="Failed to load the door log" error={error} />
      ) : (
        <Table<Scan>
          rowKey="id"
          loading={loading}
          dataSource={data?.ticketScanHistory.entities || []}
          onChange={table.onChange}
          scroll={{ x: 900 }}
          locale={{
            emptyText:
              debouncedSearch || results.length
                ? 'No scans match these filters.'
                : 'No tickets scanned yet.',
          }}
          pagination={{
            current: table.page,
            pageSize: table.pageSize,
            total: data?.ticketScanHistory.total || 0,
            showSizeChanger: false,
            showTotal: (count) => pluralize(count, 'scan'),
          }}
          columns={[
            {
              title: 'Scanned',
              key: 'scannedAt',
              sorter: true,
              sortOrder: table.sortOrder('scannedAt'),
              render: (_, scan) =>
                formatEventDateTime(scan.scannedAt, event.timezone),
            },
            {
              title: 'Result',
              key: 'result',
              sorter: true,
              sortOrder: table.sortOrder('result'),
              render: (_, scan) => (
                <StatusTag meta={SCAN_RESULT_META[scan.result]} />
              ),
            },
            {
              title: 'Ticket',
              key: 'ticket',
              render: (_, scan) =>
                scan.ticket ? (
                  <div>
                    <Text code>{scan.ticket.serial}</Text>
                    <div>
                      <Text type="secondary">{scan.ticket.tierLabel}</Text>
                    </div>
                  </div>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Holder',
              key: 'holder',
              render: (_, scan) => scan.ticket?.holderName || '—',
            },
            {
              title: 'Gate',
              key: 'gate',
              render: (_, scan) => scan.gate || '—',
            },
            {
              title: 'Message',
              dataIndex: 'message',
              key: 'message',
            },
          ]}
        />
      )}
    </>
  );
};

export default DoorLogTab;
